import React, { useState, useEffect } from "react";
import {
  Header,
} from "../components/Header";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarToday from '@mui/icons-material/CalendarToday';
import ClassIcon from '@mui/icons-material/Class';

import { getCourses } from "../services/getCourses";
import { getEval } from "../services/getEval";
import useUserStore from "../store/UserStore";
import useSelectionsStore from "../store/UserSelectionsStore.js";
import { useTranslation } from "react-i18next";
import { UsuariListButton } from '../components/UsuariListButton.jsx';
import { saveUserAction } from "../services/saveUserAction.js";

export function Evaluacion({ setIsAuthenticated }) {
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);
  const [courses, setCourses] = useState([]);
  const selections = useSelectionsStore((state) => state.selections);
  const setSelections = useSelectionsStore((state) => state.setSelections);
  const [showYearSelection, setShowYearSelection] = useState(true);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelections({});
  }, []);

  useEffect(() => {
    getCourses()
      .then((response) => {
        const orderedCourses = response.data.sort((a, b) => b.curso.codeje - a.curso.codeje).reverse();
        setCourses(orderedCourses);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching courses:", error);
        setLoading(false);
      });
  }, [user.idpersona]);

  const handleCodejeSelect = (codeje) => {
    setSelections({
      ...selections,
      codeje: codeje,
    });
    setShowYearSelection(false);
  };

  const handleCourseSelect = async (courseId) => {
    setSelections({
      ...selections,
      course: courseId,
    });

    try {
      setLoading(true);
      setError(false);

      const response = await getEval(courseId);

      if (response?.data?.message !== 'Error al obtener la evaluacion del alumno') {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Evaluacion_${courseId}.pdf`);
        document.body.appendChild(link);

        link.click();
        
        saveUserAction({ id_accion: 6, detalles_accion: `Descarga de evaluación ${courseId} exitosa` });

        link.remove();

        setLoading(false);

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 5000);

      } else {
        saveUserAction({ id_accion: 6, detalles_accion: `Error al descargar evaluación ${courseId}` });
        setLoading(false);
        setError(true);
      }
    } catch (error) {
      console.error("Error fetching eval:", error);
      saveUserAction({ id_accion: 6, detalles_accion: `Error al descargar evaluación ${courseId}` });
      setLoading(false);
      setError(true);
    }
  };

  const handleGoBackToYears = () => {
    setShowYearSelection(true);
    setSelections({ ...selections, codeje: null });
  };

  let codeje_courses = [];
  let filteredCourses = [];

  courses.forEach((course) => {
    if (!codeje_courses.includes(course.curso.codeje)) {
      codeje_courses.push(course.curso.codeje);
    }

    if (selections.codeje && course.curso.codeje === selections.codeje) {
      filteredCourses.push(course);
    }
  });

  return (
    loading ? 
    (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ 
        color: 'rgba(80, 165, 121, 0.7)',
       }}/>
    </Box>) :

    <Box sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      width: "100%",
    }}>
      <Header setIsAuthenticated={setIsAuthenticated} />


      {showYearSelection
        ? (
          <Box sx={{ flexGrow: 1, padding: 3, width: "100%", margin: "auto" }}>
            <Typography variant="h4" sx={{ mb: 4, color: "black", textAlign: "center" }}>
              {t("userSelection.selectUser")} <span style={{ color: "#50A579" }}>año</span>
            </Typography>
            <List sx={{ width: "100%", maxWidth: 540 }}>
              {codeje_courses.map((codeje) => (
                <ListItem key={codeje} disablePadding>
                  <UsuariListButton onClick={() => handleCodejeSelect(codeje)}>
                    <ListItemIcon>
                      <CalendarToday sx={{ color: "#50A579" }} />
                    </ListItemIcon>
                    <ListItemText primary={codeje} />
                  </UsuariListButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, padding: 3, width: "100%", margin: "auto" }}>
            <Typography variant="h4" sx={{ mb: 3, color: "black", textAlign: "center" }}>
              {t("userSelection.selectUser")} <span style={{ color: "#50A579" }}>curso</span>
            </Typography>

            <IconButton
              aria-label="back"
              onClick={handleGoBackToYears}
              sx={{ mb: 3 }}
            >
              <ArrowBackIcon /> <Typography sx={{ ml: 2 }} variant="body1">Volver</Typography>
            </IconButton>

            <List sx={{ width: "100%", maxWidth: 540 }}>
              {filteredCourses.map((course) => (
                <ListItem key={course.idcurso} disablePadding>
                  <UsuariListButton onClick={() => handleCourseSelect(course.idcurso)}>
                    <ListItemIcon>
                      <ClassIcon sx={{ color: "#50A579" }} />
                    </ListItemIcon>
                    <ListItemText primary={course.curso ? course.curso.descripcion : course.idcurso} />
                  </UsuariListButton>
                </ListItem>
              ))}
            </List>
            {error && (
            <Snackbar
              open={error}
              autoHideDuration={6000}
              onClose={() => setError(false)}
            >
              <Alert onClose={() => setError(false)} severity="error" sx={{ width: '100%' }}>
                {t("evaluation.noEvaluations")}
              </Alert>
            </Snackbar>
            )}
          </Box>
        )}
    </Box>
  );
}
